var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useState } from 'react';
import MuiAutocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { debounce } from '@mui/material/utils';
const getOptionLabelDefault = (option) => {
    return typeof option === 'string' ? option : option.label;
};
export const AsyncAutocomplete = (_a) => {
    var { name, label, placeholder, optionsFetcher, errorMessage, getOptionLabel, multiple, onChange, showAllOptions } = _a, rest = __rest(_a, ["name", "label", "placeholder", "optionsFetcher", "errorMessage", "getOptionLabel", "multiple", "onChange", "showAllOptions"]);
    const [value, setValue] = useState(rest.defaultValue ? rest.defaultValue : null);
    const [values, setValues] = useState(multiple && rest.defaultValue ? rest.defaultValue : []);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const fetchData = useMemo(() => debounce((searchTerm) => __awaiter(void 0, void 0, void 0, function* () {
        if (!showAllOptions && !searchTerm) {
            setOptions([]);
            return;
        }
        try {
            const data = yield optionsFetcher(searchTerm || '');
            setOptions(data);
        }
        catch (err) {
            console.error('Error retrieving search results for autocomplete', err, name);
        }
    }), 250), [optionsFetcher, name, showAllOptions]);
    useEffect(() => {
        let active = true;
        if (!showAllOptions && inputValue === '') {
            if (multiple) {
                setOptions(values);
            }
            else {
                setOptions(value ? [value] : []);
            }
            return undefined;
        }
        if (active) {
            fetchData(inputValue);
        }
        return () => {
            active = false;
        };
    }, [value, values, inputValue, fetchData, multiple, showAllOptions]);
    return (React.createElement(Stack, null,
        React.createElement(MuiAutocomplete, Object.assign({ multiple: multiple, filterOptions: (x) => x, options: options, value: multiple ? values : value, isOptionEqualToValue: (option, value) => option.id === value.id, onChange: (event, newValue) => {
                onChange === null || onChange === void 0 ? void 0 : onChange(event, newValue);
                if (multiple) {
                    if (Array.isArray(newValue)) {
                        const newValues = newValue;
                        setValues(newValues);
                        if (!showAllOptions) {
                            setOptions(newValues ? [...newValues, ...options] : options);
                        }
                    }
                }
                else {
                    if (typeof newValue !== 'string' && !Array.isArray(newValue)) {
                        setValue(newValue);
                        setOptions(newValue ? [newValue, ...options] : options);
                    }
                }
            }, onInputChange: (_event, newInputValue) => {
                setInputValue(newInputValue);
            }, renderInput: (params) => {
                return (React.createElement(TextField, Object.assign({}, params, { label: label, placeholder: values.length === 0 && !value ? placeholder : undefined, fullWidth: true, error: !!errorMessage, helperText: errorMessage })));
            }, getOptionLabel: getOptionLabel || getOptionLabelDefault }, rest)),
        multiple ? (values.map((val) => React.createElement("input", { key: val.id, type: "hidden", value: val.id, name: `${name}[]` }))) : (React.createElement("input", { type: "hidden", value: (value === null || value === void 0 ? void 0 : value.id) || '', name: name }))));
};
